import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { isBefore } from 'date-fns';
import { isEqual } from 'lodash-es';
import { extractQuillDelta, isEmptyRichText } from 'seagull/utils/richText';
import { notEmpty } from '~/infra/utils';
import { ServiceChangeItemType } from '../service/model';
import { RecurrencePeriodTypes } from '../service-billing/model';
import * as serviceBillingModel from '../service-billing/selectors';
import { isFixedPrice, isRangePrice, isVariablePrice } from '../service-cost/pricing';
import { finalPrice, isAmended } from './selectors';
function nullablesAreNotEqual(nullable1, nullable2, equalFn) {
    if (nullable1 != null && nullable2 == null || nullable1 == null && nullable2 != null || nullable1 != null && nullable2 != null && !equalFn(nullable1, nullable2)) {
        return true;
    }
    return false;
}
function servicePricingChangeItem(service) {
    const currentCost = service.cost;
    if (isAmended(service.status) && service.status.amended.changes.cost != null) {
        const { cost: changedCost } = service.status.amended.changes;
        if (isRangePrice(changedCost) && isRangePrice(currentCost)) {
            if (nullablesAreNotEqual(changedCost.minPrice, currentCost.minPrice, (minPrice1, minPrice2)=>minPrice1.equals(minPrice2)) || !changedCost.maxPrice.equals(currentCost.maxPrice)) return {
                type: ServiceChangeItemType.Pricing,
                requiresApproval: changedCost.maxPrice.greaterThan(currentCost.maxPrice) || changedCost.minPrice != null && (currentCost.minPrice == null || changedCost.minPrice.greaterThan(currentCost.minPrice)),
                value: changedCost
            };
        } else if (isFixedPrice(changedCost) && isFixedPrice(currentCost)) {
            if (!changedCost.price.equals(currentCost.price)) return {
                type: ServiceChangeItemType.Pricing,
                requiresApproval: changedCost.price.greaterThan(currentCost.price),
                value: changedCost
            };
        } else if (isVariablePrice(changedCost) && isVariablePrice(currentCost)) {
            if (nullablesAreNotEqual(changedCost.unitCap, currentCost.unitCap, (unitCap1, unitCap2)=>unitCap1.equals(unitCap2)) || !changedCost.price.equals(currentCost.price) || changedCost.unitName !== currentCost.unitName) return {
                type: ServiceChangeItemType.Pricing,
                requiresApproval: changedCost.price.greaterThan(currentCost.price) || changedCost.unitName !== currentCost.unitName || currentCost.unitCap != null && (changedCost.unitCap == null || changedCost.unitCap.greaterThan(currentCost.unitCap)),
                value: changedCost
            };
        } else if (changedCost.pricingType !== currentCost.pricingType) {
            return {
                type: ServiceChangeItemType.Pricing,
                requiresApproval: true,
                value: changedCost
            };
        }
    }
    return null;
}
function serviceDiscountChangeItem(service) {
    if (isAmended(service.status) && service.status.amended.changes.cost != null && nullablesAreNotEqual(service.status.amended.changes.cost.discount, service.cost.discount, (discount1, discount2)=>discount1.isSameAs(discount2))) {
        const updatedCost = service.status.amended.changes.cost;
        const origPrice = finalPrice(service, 1);
        const newPrice = finalPrice({
            cost: updatedCost
        }, 1);
        return {
            type: ServiceChangeItemType.Discount,
            requiresApproval: newPrice.greaterThan(origPrice),
            value: service.status.amended.changes.cost.discount
        };
    } else {
        return null;
    }
}
function isSingleTrigger(billing) {
    return serviceBillingModel.accept(billing, {
        visitOnApproval: ()=>true,
        visitOneTimeManual: ()=>true,
        visitOneTimeInStages: ()=>true,
        visitOngoing: ()=>false,
        visitRepeatableManual: ()=>false,
        visitOnDemand: ()=>false
    });
}
function invoicesPerYear(period) {
    switch(period){
        case RecurrencePeriodTypes.Weekly:
            return 52;
        case RecurrencePeriodTypes.BiWeekly:
            return 26;
        case RecurrencePeriodTypes.Monthly:
            return 12;
        case RecurrencePeriodTypes.Quarterly:
            return 4;
        case RecurrencePeriodTypes.Yearly:
            return 1;
    }
}
function isPeriodIncreased(before, after) {
    const handleBeforeRecurring = (before)=>{
        const handleBeforeAndAfterRecurring = (after)=>invoicesPerYear(after.recurrencePeriod) > invoicesPerYear(before.recurrencePeriod);
        return serviceBillingModel.accept(after, {
            visitOnApproval: ()=>false,
            visitOneTimeManual: ()=>false,
            visitOneTimeInStages: ()=>false,
            visitOngoing: handleBeforeAndAfterRecurring,
            visitRepeatableManual: handleBeforeAndAfterRecurring,
            visitOnDemand: ()=>false
        });
    };
    return serviceBillingModel.accept(before, {
        visitOnApproval: ()=>false,
        visitOneTimeManual: ()=>false,
        visitOneTimeInStages: ()=>false,
        visitRepeatableManual: handleBeforeRecurring,
        visitOngoing: handleBeforeRecurring,
        visitOnDemand: ()=>false
    });
}
function serviceBillingChangeItem(service) {
    if (isAmended(service.status) && service.status.amended.changes.billing != null && !isEqual(service.status.amended.changes.billing, service.billing)) {
        const originalBilling = service.billing;
        const changedBilling = service.status.amended.changes.billing;
        return {
            type: ServiceChangeItemType.BillingTrigger,
            requiresApproval: isSingleTrigger(originalBilling) && !isSingleTrigger(changedBilling) || isPeriodIncreased(originalBilling, changedBilling),
            value: changedBilling
        };
    } else {
        return null;
    }
}
function serviceDescriptionChangeItem(service) {
    if (isAmended(service.status) && service.status.amended.changes.description != null && isRichTextDescriptionChanged(service.description, service.status.amended.changes.description)) {
        return {
            type: ServiceChangeItemType.Description,
            requiresApproval: true,
            value: service.status.amended.changes.description
        };
    } else {
        return null;
    }
}
function serviceTermsChangeItem(service) {
    if (isAmended(service.status) && service.status.amended.changes.terms != null && isRichTextDescriptionChanged(service.terms, service.status.amended.changes.terms)) {
        return {
            type: ServiceChangeItemType.Terms,
            requiresApproval: false,
            value: service.status.amended.changes.terms
        };
    }
    return bundleTermsChangeItem(service);
}
function bundleTermsChangeItem(service) {
    if (!isAmended(service.status)) return null;
    const amendedBundle = service.status.amended.changes.bundle;
    if (amendedBundle == null || service.bundle == null) return null;
    const amendedItems = amendedBundle.services;
    const originalItems = service.bundle.services;
    if (amendedItems.length !== originalItems.length) return null;
    const anyTermsChanged = amendedItems.some((amendedItem, index)=>{
        const originalItem = originalItems[index];
        if (originalItem == null) return false;
        return isRichTextDescriptionChanged(originalItem.terms, amendedItem.terms);
    });
    if (!anyTermsChanged) return null;
    const allAmendedTermsEmpty = amendedItems.every((item)=>isEmptyRichTextDescription(item.terms));
    return {
        type: ServiceChangeItemType.Terms,
        requiresApproval: false,
        value: allAmendedTermsEmpty ? '' : joinBundleTerms(amendedItems)
    };
}
function joinBundleTerms(items) {
    return items.map((item)=>item.terms).filter((terms)=>!!terms).join('; ');
}
export function isRichTextDescriptionChanged(newDesc, oldDesc) {
    if (!oldDesc) {
        return !isEmptyRichTextDescription(newDesc);
    } else if (!newDesc) {
        return !isEmptyRichTextDescription(oldDesc);
    } else {
        return oldDesc !== newDesc;
    }
}
function isEmptyRichTextDescription(desc) {
    if (!desc) {
        return true;
    }
    const delta = extractQuillDelta(desc);
    if (delta && isEmptyRichText(delta)) {
        return true;
    }
    return false;
}
function includedServicesDescriptionChangeItem(service) {
    if (isAmended(service.status) && service.status.amended.changes.bundle != null && service.bundle != null) {
        const changedNumServices = service.bundle.services.length !== service.status.amended.changes.bundle.services.length;
        const changedServiceNameOrDescription = service.status.amended.changes.bundle.services.some((s, index)=>{
            if (!service.bundle || !service.bundle.services[index]) return false;
            const { name: newName, description: newDescription } = s;
            const { name: originalName, description: originalDescription } = service.bundle.services[index];
            return newName !== originalName || isRichTextDescriptionChanged(newDescription, originalDescription);
        });
        if (changedNumServices || changedServiceNameOrDescription) {
            return {
                type: ServiceChangeItemType.IncludedServicesDescription,
                requiresApproval: false,
                value: service.status.amended.changes.bundle.services
            };
        } else {
            return null;
        }
    } else {
        return null;
    }
}
function servicePriceIncreaseChangeItem(service) {
    if (isAmended(service.status) && service.status.amended.changes.cost != null && !isEqual(service.status.amended.changes.cost.priceIncrease, service.cost.priceIncrease)) {
        const newValue = service.status.amended.changes.cost.priceIncrease;
        const oldValue = service.cost.priceIncrease;
        const newIncreaseOn = newValue.increaseOn();
        const oldIncreaseOn = oldValue.increaseOn();
        const addingPriceIncrease = newValue.exists() && !oldValue.exists();
        const hasPriceIncrease = oldValue.exists() && newValue.exists() && newValue.percentIncrease().gt(oldValue.percentIncrease());
        const isSoonerPriceIncrease = !!newIncreaseOn && !!oldIncreaseOn && isBefore(newIncreaseOn, oldIncreaseOn);
        return {
            type: ServiceChangeItemType.PriceIncrease,
            requiresApproval: addingPriceIncrease || hasPriceIncrease || isSoonerPriceIncrease,
            value: newValue
        };
    } else {
        return null;
    }
}
export function makeServiceChangeList(service) {
    return [
        servicePricingChangeItem,
        serviceDiscountChangeItem,
        serviceDescriptionChangeItem,
        serviceTermsChangeItem,
        includedServicesDescriptionChangeItem,
        serviceBillingChangeItem,
        servicePriceIncreaseChangeItem
    ].map((fn)=>fn(service)).filter(notEmpty);
}
export function serviceChangeListToChanges(changeList, service) {
    if (changeList.length === 0) return null;
    const changedPricing = changeList.find((param)=>{
        let { type } = param;
        return type === ServiceChangeItemType.Pricing;
    });
    const changedDiscount = changeList.find((param)=>{
        let { type } = param;
        return type === ServiceChangeItemType.Discount;
    });
    const cost = changedPricing || changedDiscount ? _object_spread_props(_object_spread({}, (changedPricing === null || changedPricing === void 0 ? void 0 : changedPricing.value) || service.cost), {
        discount: (changedDiscount === null || changedDiscount === void 0 ? void 0 : changedDiscount.value) || service.cost.discount
    }) : null;
    return Object.assign({
        name: null,
        note: null,
        cost,
        billing: null,
        description: null
    }, ...changeList.map((item)=>{
        switch(item.type){
            case ServiceChangeItemType.BillingTrigger:
                return {
                    billing: item.value
                };
            case ServiceChangeItemType.Description:
                return {
                    description: item.value
                };
            case ServiceChangeItemType.Terms:
                return {
                    terms: item.value
                };
            default:
                return {};
        }
    }));
}
